import React, { useState, useEffect, lazy, Suspense } from 'react'; 
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import Header from './components/Header';
import FooterNav from './components/FooterNav';
import UploadModal from './components/UploadModal';
import AuthForm from './components/AuthForm';
import BookDetail from './components/BookDetail';
const BookReaderPage = lazy(() => import('./components/BookReaderPage'));
const AIChatPage = lazy(() => import('./components/AIChatPage'));
const PaymentPage = lazy(() => import('./components/PaymentPage'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const MonetizationPage = lazy(() => import('./components/MonetizationPage'));
const ProfilPage = lazy(() => import('./components/ProfilPage'));
const SubscriptionsPage = lazy(() => import('./components/SubscriptionsPage'));
const LibraryPage = lazy(() => import('./components/LibraryPage'));
const ChannelAnalyticsPage = lazy(() => import('./components/ChannelAnalyticsPage'));
const CreatorProfilePage = lazy(() => import('./components/CreatorProfilePage'));
const MessagesPage = lazy(() => import('./components/MessagesPage'));
const NotificationsPage = lazy(() => import('./components/NotificationsPage'));
const ContactsDiscoveryPage = lazy(() => import('./components/ContactsDiscoveryPage'));
import InAppNotice from './components/InAppNotice';
import Ads from './components/Ads';
import { notify } from './utils/notify';

import './App.css';
import { API_BASE } from './config/api';
import { setupAutoTranslate } from './utils/autoTranslate';

const Loader = () => (
  <div className="boot-loader-overlay" role="status" aria-label="Chargement">
    <div className="boot-orbit">
      <div className="boot-dot dot-a" />
      <div className="boot-dot dot-b" />
    </div>
  </div>
);

/* ==============================
   HOME COMPONENT
============================== */
function Home({
  books,
  handleLike,
  handleSubscribe,
  subscriptions,
  searchQuery,
  user,
  recommendedBooks,
  recommendationsLoading,
  recommendationsError
}) {
  const navigate = useNavigate();

  // Filtrer livres : on prend maintenant tous les livres publics
  const filteredBooks = books.filter((b) =>
    b.visibility !== 'private' && // on ignore juste les livres privés
    (
      b.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.publisher_name?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="page">
      {user && recommendedBooks.length > 0 && !recommendationsLoading && !recommendationsError && (
        <section className="recommendations-section">
          <div className="book-grid">
            {recommendedBooks.map((book) => (
              <div
                key={`rec-${book.id}`}
                className="book-card"
                onClick={() => navigate(`/book/${book.id}`)}
              >
                <img
                  src={`${API_BASE}/${book.cover}`}
                  alt={book.title}
                  className="book-cover-home"
                  loading="lazy"
                />
                <div className="book-bottom-info">
                  <div className="author-info">
                    <img
                      src={(book.publisher_profile || book.author_profile) ? `${API_BASE}/${book.publisher_profile || book.author_profile}` : `${API_BASE}/uploads/profiles/default-profile.png`}
                      alt={book.publisher_name || book.author}
                      className="author-profile-home"
                      loading="lazy"
                    />
                    <div className="author-text">
                      <span className="author-name">{book.publisher_name || book.author}</span>
                      <span className="book-title">{book.title}</span>
                    </div>
                  </div>
                  <small className="recommendation-score">
                    Score: {(Number(book.score) || 0).toFixed(2)}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bloc pub page d'accueil */}
      <div style={{ margin: '16px 0' }}>
        <Ads slot="8614215788" />
      </div>

      <div className="book-grid">
        {filteredBooks.length > 0 ? (
          filteredBooks.map((book) => (
            <div 
              key={book.id} 
              className="book-card"
              onClick={() => navigate(`/book/${book.id}`)}
            >
              <img 
                src={`${API_BASE}/${book.cover}`} 
                alt={book.title} 
                className="book-cover-home"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 520px) 100vw, 480px"
              />
              <div className="book-bottom-info">
                <div className="author-info">
                  <img 
                    src={(book.publisher_profile || book.author_profile) ? `${API_BASE}/${book.publisher_profile || book.author_profile}` : `${API_BASE}/uploads/profiles/default-profile.png`} 
                    alt={book.publisher_name || book.author} 
                    className="author-profile-home"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="author-text">
                    <span className="author-name">{book.publisher_name || book.author}</span>
                    <span className="book-title">{book.title}</span>
                  </div>
                </div>
                <button
                  className="subscribe-btn-home"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubscribe(book.author_id);
                  }}
                >
                  {subscriptions.includes(book.author_id) ? '✓' : '+'}
                </button>
              </div>
              <div className="actions">
                <small>👁️ {book.views || 0} vues</small>
              </div>
            </div>
          ))
        ) : null}
      </div>
    </div>
  );
}

/* ==============================
   APP PRINCIPALE
============================== */
function App() {
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [books, setBooks] = useState([]);
  const [bootLoading, setBootLoading] = useState(true);
  const [likedBooks, setLikedBooks] = useState([]);
  const [readBooks, setReadBooks] = useState([]);
  const [recommendedBooks, setRecommendedBooks] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationsError, setRecommendationsError] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('booktubeSettings') || '{}');
      return {
        theme: saved.theme || 'dark',
        language: saved.language || (navigator.language || 'fr'),
        notifications: saved.notifications ?? true,
        autoplay: saved.autoplay ?? true,
        compactMode: saved.compactMode ?? false,
        reduceMotion: saved.reduceMotion ?? false
      };
    } catch {
      return {
        theme: 'dark',
        language: navigator.language || 'fr',
        notifications: true,
        autoplay: true,
        compactMode: false,
        reduceMotion: false
      };
    }
  });

  // ====== CHARGER LIVRES ======
  const fetchBooks = async () => {
    try {
      const res = await axios.get(`${API_BASE}/books`);
      const booksWithFallback = res.data.map(b => ({
        ...b,
        cover: b.cover || 'uploads/covers/default-cover.png',
        author_profile: b.author_profile || 'uploads/profiles/default-profile.png',
        publisher_profile: b.publisher_profile || '',
        publisher_name: b.publisher_name || '',
        title: b.title || 'Titre non disponible',
        author: b.author || 'Auteur inconnu',
        visibility: b.visibility || 'public',
        views: b.views || 0
      }));
      setBooks(booksWithFallback);
      console.log("Livres chargés:", booksWithFallback);
    } catch (err) {
      console.error("Erreur chargement livres:", err);
    }
  };

  useEffect(() => {
    fetchBooks().finally(() => setBootLoading(false));
  }, []);

  useEffect(() => {
    localStorage.setItem('booktubeSettings', JSON.stringify(settings));
    document.documentElement.lang = settings.language || 'fr';
    document.documentElement.setAttribute('data-theme', settings.theme === 'light' ? 'light' : 'dark');
    document.documentElement.setAttribute('data-compact', settings.compactMode ? '1' : '0');
    document.documentElement.setAttribute('data-reduce-motion', settings.reduceMotion ? '1' : '0');
  }, [settings]);

  useEffect(() => {
    const cleanup = setupAutoTranslate(settings.language || navigator.language || 'fr');
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [settings.language]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowInstall(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setShowInstall(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // ====== CHARGER UTILISATEUR ET ABONNEMENTS ======
  useEffect(() => {
    const savedUser = localStorage.getItem("booktubeUser");
    if (savedUser) {
      const u = JSON.parse(savedUser);
      setUser(u);

      axios.get(`${API_BASE}/api/users/me?email=${u.email}`)
        .then(res => setUser(res.data))
        .catch(err => console.error("Erreur chargement utilisateur:", err));

      axios.get(`${API_BASE}/api/subscribe?user_email=${u.email}`)
        .then(res => setSubscriptions(res.data))
        .catch(err => console.error("Erreur chargement abonnements:", err));
    }

    const savedRead = localStorage.getItem("booktubeReadBooks");
    if (savedRead) setReadBooks(JSON.parse(savedRead));
  }, []);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!user?.email) {
        setRecommendedBooks([]);
        setRecommendationsError('');
        return;
      }

      try {
        setRecommendationsLoading(true);
        setRecommendationsError('');
        const res = await axios.get(`${API_BASE}/api/recommendations`, {
          params: { topN: 10, user_email: user.email }
        });

        const recommendations = (res.data?.recommendations || []).map((b) => ({
          ...b,
          cover: b.cover || 'uploads/covers/default-cover.png',
          author_profile: b.author_profile || 'uploads/profiles/default-profile.png',
          publisher_profile: b.publisher_profile || '',
          publisher_name: b.publisher_name || ''
        }));

        setRecommendedBooks(recommendations);
      } catch (err) {
        console.error("Erreur chargement recommandations:", err);
        setRecommendationsError('Impossible de charger les recommandations pour le moment.');
      } finally {
        setRecommendationsLoading(false);
      }
    };

    fetchRecommendations();
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) return;
    const query = searchQuery.trim();
    if (query.length < 2) return;

    const timer = setTimeout(() => {
      axios.post(`${API_BASE}/api/activity/search`, {
        user_email: user.email,
        query
      }).catch(() => {});
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, user?.email]);

  // ====== AUTRES FONCTIONS ======
  const handleUploadBook = async (formData) => {
    try {
      await axios.post(`${API_BASE}/upload-book`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchBooks();
      notify('Livre publie avec succes, fait maintenant une promotion pour attirer les spectacteurs !');
    } catch (err) {
      notify('Erreur lors de la publication: ' + (err.response?.data?.error || err.message));
    }
  };

  const getGuestLikeToken = () => {
    let token = localStorage.getItem('booktubeGuestLikeToken');
    if (!token) {
      token = (window.crypto?.randomUUID?.() || `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem('booktubeGuestLikeToken', token);
    }
    return token;
  };

  const handleLike = async (id) => {
    if (!likedBooks.includes(id)) {
      try {
        await axios.put(`${API_BASE}/api/books/${id}/like`, {
          user_email: user?.email || '',
          guest_token: user?.email ? '' : getGuestLikeToken()
        });
        setLikedBooks([...likedBooks, id]);
        fetchBooks();
      } catch {
        notify("Erreur lors du like");
      }
    }
  };

  const handleSubscribe = async (authorId) => {
    console.log("handleSubscribe called with authorId:", authorId, "user:", user);
    if (!subscriptions.includes(authorId) && user) {
      try {
        console.log("Sending subscribe request with user_email:", user.email, "author_id:", authorId);
        await axios.post(`${API_BASE}/api/subscribe`, {
          user_email: user.email,
          author_id: authorId
        });
        const newSubs = [...subscriptions, authorId];
        setSubscriptions(newSubs);
        localStorage.setItem("booktubeSubscriptions", JSON.stringify(newSubs));
      } catch (err) {
        notify("Erreur lors de l'abonnement: " + (err.response?.data?.error || err.message));
      }
    } else {
      console.log("Not subscribing: subscriptions includes authorId?", subscriptions.includes(authorId), "user exists?", !!user);
    }
  };

  const markAsRead = (bookId) => {
    if (!readBooks.includes(bookId)) {
      const newRead = [...readBooks, bookId];
      setReadBooks(newRead);
      localStorage.setItem("booktubeReadBooks", JSON.stringify(newRead));
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  const updateSettings = (partial) => {
    setSettings((prev) => ({ ...prev, ...partial }));
  };

  const AppContent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isReaderPage = /^\/book\/[^/]+\/read$/.test(location.pathname);
    const isAiChatPage = /^\/book\/[^/]+\/ai-chat$/.test(location.pathname);
    const isHomePage = location.pathname === '/';
    const isPaymentPage = location.pathname === '/payment';
    const isUploadPage = location.pathname === '/upload';
    const isSettingsPage = location.pathname === '/settings';
    const isMessagesPage = location.pathname === '/messages';
    const isNotificationsPage = location.pathname === '/notifications';
    const isContactsPage = location.pathname === '/contacts-discovery';
    const hideAppChrome = isReaderPage || isAiChatPage || isPaymentPage || isUploadPage || isSettingsPage || isMessagesPage || isNotificationsPage || isContactsPage;
    const showLoader = bootLoading || (recommendationsLoading && isHomePage);

    return (
      <div className={`App ${hideAppChrome ? 'no-chrome' : ''}`}>
        {showLoader && <Loader />}
        {!hideAppChrome && <Header user={user} onSearch={handleSearch} books={books} />}

        <div className="route-content">
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route
                path="/"
                element={
                  <Home
                    books={books}
                    handleLike={handleLike}
                    handleSubscribe={handleSubscribe}
                    subscriptions={subscriptions}
                    searchQuery={searchQuery}
                    user={user}
                    recommendedBooks={recommendedBooks}
                    recommendationsLoading={recommendationsLoading}
                    recommendationsError={recommendationsError}
                  />
                }
              />

              <Route path="/book/:id" element={<BookDetail user={user} markAsRead={markAsRead} />} />
              <Route path="/book/:id/read" element={<BookReaderPage user={user} markAsRead={markAsRead} />} />
              <Route path="/book/:id/ai-chat" element={<AIChatPage user={user} />} />
              <Route path="/messages" element={<MessagesPage user={user} />} />
              <Route path="/contacts-discovery" element={<ContactsDiscoveryPage user={user} />} />
              <Route path="/notifications" element={<NotificationsPage user={user} />} />
              <Route path="/payment" element={<PaymentPage user={user} />} />
              <Route
                path="/settings"
                element={<SettingsPage settings={settings} onChange={updateSettings} />}
              />
              <Route
                path="/upload"
                element={
                  <UploadModal
                    onClose={() => navigate(-1)}
                    onUpload={handleUploadBook}
                    user={user}
                    asPage
                  />
                }
              />
              <Route path="/account" element={!user ?
                <AuthForm onLogin={(userData) => {
                  setUser(userData);
                  localStorage.setItem("booktubeUser", JSON.stringify(userData));
                }} />
                : <ProfilPage settings={settings} />}
              />

              <Route
                path="/subscriptions"
                element={
                  <SubscriptionsPage
                    user={user}
                    subscriptions={subscriptions}
                    books={books}
                  />
                }
              />
              <Route
                path="/abonnement"
                element={
                  <SubscriptionsPage
                    user={user}
                    subscriptions={subscriptions}
                    books={books}
                  />
                }
              />
              <Route
                path="/library"
                element={
                  <LibraryPage
                    books={books}
                    readBooks={readBooks}
                    user={user}
                    onBookDeleted={fetchBooks}
                  />
                }
              />
              <Route
                path="/bibliotheque"
                element={
                  <LibraryPage
                    books={books}
                    readBooks={readBooks}
                    user={user}
                    onBookDeleted={fetchBooks}
                  />
                }
              />
              <Route
                path="/vous"
                element={!user ?
                  <AuthForm onLogin={(userData) => {
                    setUser(userData);
                    localStorage.setItem("booktubeUser", JSON.stringify(userData));
                  }} />
                  : <ProfilPage settings={settings} />}
              />

              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/monetization" element={<MonetizationPage />} />
              <Route path="/channel-analytics" element={<ChannelAnalyticsPage />} />
              <Route path="/creator/:id" element={<CreatorProfilePage />} />
            </Routes>
          </Suspense>
        </div>
        {!hideAppChrome && (
          <FooterNav
            onHome={() => navigate('/')}
            onSubscriptions={() => navigate('/abonnement')}
            onAccount={() => navigate('/vous')}
            onUpload={() => navigate('/upload')}
            onLibrary={() => navigate('/bibliotheque')}
          />
        )}

        {!hideAppChrome && showInstall && (
          <button className="install-button" onClick={handleInstallClick}>
            Installer BookTube
          </button>
        )}
      </div>
    );
  };

  return (
    <Router>
      <InAppNotice />
      <AppContent />
    </Router>
  );
}

export default App;


